import {
  Users,
  Factory,
  Store,
  Handshake,
  Users2,
  UserCog,
  FileSpreadsheet,
  Landmark,
  Scale,
  ShieldCheck,
  Briefcase,
} from 'lucide-react';

// Visual config for the built-in cari type codes. Custom (admin-created)
// types fall back to DEFAULT_CARI_TYPE_DISPLAY — types themselves are dynamic
// registry rows served by tradingCariType.list.
export interface CariTypeDisplay {
  icon: typeof Users;
  bgColor: string;
  iconColor: string;
  badgeColor: string;
  hoverBorder: string;
}

export const CARI_TYPE_DISPLAY: Record<string, CariTypeDisplay> = {
  M: { icon: Users, bgColor: 'bg-blue-100', iconColor: 'text-blue-600', badgeColor: 'bg-blue-100 text-blue-700', hoverBorder: 'hover:border-blue-400' },
  T: { icon: Factory, bgColor: 'bg-orange-100', iconColor: 'text-orange-600', badgeColor: 'bg-orange-100 text-orange-700', hoverBorder: 'hover:border-orange-400' },
  S: { icon: Store, bgColor: 'bg-purple-100', iconColor: 'text-purple-600', badgeColor: 'bg-purple-100 text-purple-700', hoverBorder: 'hover:border-purple-400' },
  B: { icon: Handshake, bgColor: 'bg-green-100', iconColor: 'text-green-600', badgeColor: 'bg-green-100 text-green-700', hoverBorder: 'hover:border-green-400' },
  ORT: { icon: Users2, bgColor: 'bg-amber-100', iconColor: 'text-amber-600', badgeColor: 'bg-amber-100 text-amber-700', hoverBorder: 'hover:border-amber-400' },
  P: { icon: UserCog, bgColor: 'bg-teal-100', iconColor: 'text-teal-600', badgeColor: 'bg-teal-100 text-teal-700', hoverBorder: 'hover:border-teal-400' },
  AH: { icon: FileSpreadsheet, bgColor: 'bg-slate-100', iconColor: 'text-slate-600', badgeColor: 'bg-slate-100 text-slate-700', hoverBorder: 'hover:border-slate-400' },
  E: { icon: Landmark, bgColor: 'bg-red-100', iconColor: 'text-red-600', badgeColor: 'bg-red-100 text-red-700', hoverBorder: 'hover:border-red-400' },
  X: { icon: Scale, bgColor: 'bg-pink-100', iconColor: 'text-pink-600', badgeColor: 'bg-pink-100 text-pink-700', hoverBorder: 'hover:border-pink-400' },
  I: { icon: ShieldCheck, bgColor: 'bg-cyan-100', iconColor: 'text-cyan-600', badgeColor: 'bg-cyan-100 text-cyan-700', hoverBorder: 'hover:border-cyan-400' },
};

export const DEFAULT_CARI_TYPE_DISPLAY: CariTypeDisplay = {
  icon: Briefcase,
  bgColor: 'bg-zinc-100',
  iconColor: 'text-zinc-600',
  badgeColor: 'bg-zinc-100 text-zinc-700',
  hoverBorder: 'hover:border-zinc-400',
};

export function cariTypeDisplay(code: string): CariTypeDisplay {
  return CARI_TYPE_DISPLAY[code] ?? DEFAULT_CARI_TYPE_DISPLAY;
}

export interface CariTypeNameSource {
  code: string;
  name: string;
  nameEn?: string | null;
}

/** Display label for a registry type in the active UI language. */
export function cariTypeLabel(type: CariTypeNameSource, language: string): string {
  if (language.startsWith('en') && type.nameEn) return type.nameEn;
  return type.name;
}

// Dedicated detail pages / creation modals exist for these codes; everything
// else (built-in generics + custom types) uses the generic cari flow.
export const DEDICATED_CARI_TYPES = ['M', 'T', 'S', 'B'] as const;

export function isGenericCariType(code: string): boolean {
  return !(DEDICATED_CARI_TYPES as readonly string[]).includes(code);
}
